// savePopup.tsx
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogSurface,
  DialogTitle,
  Input,
  makeStyles,
} from "@fluentui/react-components";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import React, { Fragment, useState } from "react";
import moment, { Moment } from "moment";
import DynamicDatePicker from "../../../components/ControlledDatePicker/dynamicDatepicker";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { formatGBP } from "../../../utils/currency";

const useStyles = makeStyles({
  content: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  widthcontent: {
    width: "450px",
  },
  btnWrapper: {
    margin: "10px 0px 10px 0px",
  },
});

interface SavePopupProps {
  confirmsavepopup: boolean;
  receiptedAmount: string;
  targetedAmount: string;
  difference: string;
  setConfirmsavepopup: (open: boolean) => void;
  onSave: (data: { receipted_date: any; description?: string }) => void;
  saveError?: string;
  getReceiptlistData?: any;
}

const SavePopup = (props: SavePopupProps) => {
  const styles = useStyles();
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [showAdjustmentForm, setShowAdjustmentForm] = useState(false);
  const [description, setDescription] = useState("");
  const saveLoading = useSelector(
    (state: RootState) => state.commission.loading.postReceipt || false,
  );

  const onSelectDate = (date: Date | null) => {
    setSelectedDate(date);
  };
  const receipted = parseFloat(props.receiptedAmount);
  const targeted = parseFloat(props.targetedAmount);
  const onFormatDate = (date?: Date): string => {
    return !date ? "" : moment(date).format("DD-MM-YYYY");
  };
  const amountCalculation = () => {
    if (receipted > targeted || receipted < targeted) {
      return `Difference = ${formatGBP(props.difference)}`;
    } else if (receipted === targeted) {
      return "Amount matched";
    }
    return "";
  };
  const handleSave = async () => {
    const formattedDate = moment(selectedDate).utc();
    try {
      const success: any = await props.onSave({
        receipted_date: formattedDate,
        description: showAdjustmentForm ? description : undefined,
      });
      setShowAdjustmentForm(false);
      setDescription("");
      setSelectedDate(new Date());
      props.setConfirmsavepopup(false);
      return success;
    } catch (error: any) {
      alert(error.message);
    }
  };

  return (
    <Dialog open={props.confirmsavepopup}>
      <DialogSurface className={styles.widthcontent}>
        <DialogTitle>
          {showAdjustmentForm ? "Adjustment Commission" : "Commission Receipt"}
        </DialogTitle>
        <DialogBody className={styles.content}>
          {props.saveError && (
            <p style={{ color: "red", fontSize: "14px" }}>{props.saveError}</p>
          )}
          {!showAdjustmentForm ? (
            <>
              <p>Receipted Amount: {formatGBP(props.receiptedAmount)}</p>
              <p>Targeted Amount: {formatGBP(props.targetedAmount)}</p>
              <p>
                <b style={{ fontSize: "16px" }}>{amountCalculation()}</b>
              </p>
              <p>Do you want to save the receipt(s)? (Yes/No)</p>
            </>
          ) : (
            <>
              <p>
                Record an adjustment for the difference. Enter a description and
                effective date below.
              </p>
              <label>Description:</label>
              <Input
                placeholder="Enter description"
                value={description}
                onChange={(e, data) => setDescription(data.value)}
              />
            </>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <label>Receipt Date:</label>
            <div style={{ flex: 1 }}>
              <DynamicDatePicker
                border
                name="selected_date"
                label={null}
                required={false}
                disabled={false}
                formik={{
                  values: {
                    selected_date: selectedDate ?? null,
                  },
                  setFieldValue: (_name: string, value: Date | null) => {
                    onSelectDate(value);
                  },
                  setFieldTouched: () => { },
                  setFieldError: () => { },
                  errors: {},
                  touched: {},
                }}
              />
            </div>
          </div>
        </DialogBody>
        <DialogActions className={styles.btnWrapper}>
          {!showAdjustmentForm ? (
            <>
              <Button
                appearance="primary"
                className="asc-button-primary"
                disabled={saveLoading}
                onClick={() => {
                  if (!selectedDate) {
                    alert("Please select a receipt date.");
                    return;
                  }
                  if (receipted === targeted) {
                    setShowAdjustmentForm(false);
                    handleSave();
                  } else {
                    setShowAdjustmentForm(true);
                  }
                }}
                data-testid="save-popup-yes-button"
              >
                {saveLoading ? "Saving..." : "Yes"}
              </Button>
              <Button
                appearance="secondary"
                onClick={() => {
                  props.setConfirmsavepopup(false);
                  setSelectedDate(new Date());
                }}
              >
                No
              </Button>
            </>
          ) : (
            <>
              <Button
                appearance="primary"
                className="asc-button-primary"
                onClick={handleSave}
                disabled={saveLoading}
              >
                {saveLoading ? "Saving..." : "Save"}
              </Button>
              <Button
                appearance="secondary"
                onClick={() => {
                  setShowAdjustmentForm(false);
                  setDescription("");
                  props.setConfirmsavepopup(false);
                  setSelectedDate(new Date());
                }}
              >
                Cancel
              </Button>
            </>
          )}
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};

export default SavePopup;
